/* eslint-disable react/function-component-definition */
import { useLazyQuery } from "@apollo/client";
import Avatar from "components/core/Avatar";
import { cache } from "components/financials/modern/data/useCompanyIssuers";
import getCompanyIssuerLinkProps from "components/financials/modern/getCompanyIssuerLinkProps";
import type { ICompanyIssuersQuery } from "components/financials/modern/graphql/__generated__/CompanyIssuersQuery.generated";
import RequestCompanyIssuerWithTranslatedDescriptionQuery from "components/issuers/graphql/queries/RequestCompanyIssuerWithTranslatedDescriptionQuery";
import { encodeIssuerRefToUrl } from "components/issuers/utils";
import { Spinner } from "components/ui/Spinner";
import classNames from "components/ui/classNames";
import type { ICompanyIssuerFieldsFragment } from "lib/graphql/fragments/__generated__/CompanyIssuerFields.generated";
import { ArrowLeftIcon } from "lucide-react";
import Link from "next/link";
import { useQueryState } from "nuqs";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface ModernIssuerHeaderProps {
  issuer: Omit<ICompanyIssuerFieldsFragment, "__typename"> &
    Partial<Pick<ICompanyIssuersQuery["companyIssuers"][number], "description" | "descriptionTranslations">>;
  loading?: boolean;
}
export default function ModernIssuerHeader({ issuer, loading }: ModernIssuerHeaderProps) {
  const intl = useIntl();
  const [previousUrl] = useQueryState("previous");
  const [hotSwappedTranslationContent, setHotSwapTranslationContent] = useState("");
  const appropriateTranslation = (issuer.descriptionTranslations || []).find(
    ({ language }) => intl.locale === language || intl.locale.substring(0, 2) === language,
  );
  const [requestCompanyIssuerWithTranslatedDescription, { loading: isTranslating }] = useLazyQuery(
    RequestCompanyIssuerWithTranslatedDescriptionQuery,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted(data) {
        if (data.companyIssuers.length === 1) {
          setHotSwapTranslationContent(data.companyIssuers[0].description);
          const issuerRef = encodeIssuerRefToUrl(issuer);
          if (issuerRef) {
            cache.delete(issuerRef);
          }
        }
      },
    },
  );

  return (
    <header className="mx-auto flex flex-col gap-4">
      {previousUrl && (
        <div>
          <Link
            href={previousUrl}
            className={classNames(
              "inline-flex items-center gap-x-1 rounded-full px-2 py-1 text-sm font-medium text-blue-600",
              "hover:border-transparent hover:bg-blue-600 hover:text-white",
              "dark:text-orange-500 dark:hover:bg-orange-600 dark:hover:text-white",
            )}
          >
            <ArrowLeftIcon className="h-4 w-4" absoluteStrokeWidth={true} strokeWidth={3} />
            <span>
              <FormattedMessage defaultMessage="Voltar" id="1s+epY" description="Back button link" />
            </span>
          </Link>
        </div>
      )}

      <div className="space-y-2">
        <Link {...getCompanyIssuerLinkProps(issuer)} className="inline-flex items-center gap-x-4" target="_blank">
          <div className="inline-flex items-center gap-x-2">
            <Avatar
              src={issuer.niceLogo?.url || null}
              hashValue={issuer._id}
              alt={issuer.name}
              size={42}
              squircle={true}
              className={classNames("md:inline-flex")}
            />

            <h1
              className={classNames(
                "border-b-2 border-b-transparent text-2xl font-black text-gray-900",
                "hover:border-b-black",
                "sm:text-4xl",
                "dark:text-white dark:hover:border-b-white",
              )}
            >
              {issuer.name}
            </h1>
          </div>
          <h3
            className={classNames(
              "rounded-md px-2 py-1 text-xs font-bold",
              "bg-violet-50 text-violet-600",
              "dark:bg-orange-300/30 dark:text-orange-100",
              "text-sm sm:text-base",
            )}
          >
            {issuer.tickerSymbol.toUpperCase()}
          </h3>
          {loading && <Spinner />}
        </Link>

        <p
          className={classNames(
            "line-clamp-4 text-sm leading-6 tracking-wide text-slate-600 dark:text-slate-400",
            "sm:line-clamp-none sm:text-base sm:leading-7",
          )}
        >
          {hotSwappedTranslationContent || appropriateTranslation?.textContent || issuer.description}
        </p>

        {!hotSwappedTranslationContent && !appropriateTranslation && (
          <button
            type="button"
            disabled={isTranslating}
            className={classNames(
              "font-medium hover:underline",
              "disabled:pointer-events-none disabled:text-slate-300",
            )}
            onClick={() => {
              requestCompanyIssuerWithTranslatedDescription({
                variables: {
                  input: [{ tickerSymbol: issuer.tickerSymbol, vertical: issuer.vertical }],
                  language: intl.locale.substring(0, 2),
                },
              });
            }}
          >
            {isTranslating ? (
              <FormattedMessage defaultMessage="Traduzindo..." id="puazta" />
            ) : (
              <FormattedMessage defaultMessage="Ver tradução" id="PM2OcR" />
            )}
          </button>
        )}
      </div>
    </header>
  );
}
